import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { displayToast } from "../../utils/Toast";
import Edit from "../../assets/svgIcons/FormEdit.svg";
import Enquiry from "../../assets/svgIcons/Enquiry.svg";
import Print from "../../assets/svgIcons/FormPrint.svg";
import Delete from "../../assets/svgIcons/FormDelete.svg";
import Quotation from "../../assets/svgIcons/Quotation.svg";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import PrimaryDocument from "../../components/PrimaryDocument";
import TransactionTable from "../../components/TransactionTable";
import { useErrorHandler } from "../../services/ErrorHandler";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useSalesFormStore } from "../../services/useSalesFormStore";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  fetchJobCardByCodeAndId,
  updateJobCardStatus,
  deleteTransactionByCode,
} from "../../apis/transaction";
import { fetchRouteCard } from "../../apis/processCard";
import "../../styles/pages/formBulder/JobCard.css";

const JobCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const {
    transactionCode,
    colorCode,
    transactionId,
    backDatedEntry,
    pickupMandatory,
    isAutoTransactionNo,
  } = location.state || {};

  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableData, setTableData] = useState({
    head_fields: [],
    extra_forms: [],
    rows: [],
  });
  const [transactionData, setTransactionData] = useState({
    headDetails: {},
    itemDetails: [],
  });

  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails,
  );
  const setTransactionItems = useSalesFormStore(
    (state) => state.setTransactionItems,
  );

  const formData = useSalesFormStore((state) => state.formData);

  const clearForm = useSalesFormStore((state) => state.clearForm);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const isViewMode = Boolean(transactionId) && !isEditMode;

  useAutoRefresh(
    () => fetchFieldMasterTransactionData(transactionCode, transactionId),
    { enabled: isViewMode && Boolean(transactionId && transactionCode) },
  );

  const { clearTransactionState } = useSalesFormStore();

  const rights = usePermissions(transactionCode);

  const { handleErrorResponse } = useErrorHandler();

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCloseEditMode = () => {
    setIsEditMode(false);
  };

  const handleCloseAddMode = () => {
    navigate(-1);
    clearTransactionState();
  };

  const handleJobCardAction = async (fieldName) => {
    try {
      if (fieldName === "finish") {
        const itemDetails = transactionData?.itemDetails;
        const hasRouteCardField = tableData?.rows?.some(
          (field) => field.field_type === "ROUTE_CARD",
        );

        if (hasRouteCardField && itemDetails && itemDetails.length > 0) {
          const itemsWithoutRouteCard = itemDetails.filter(
            (item) => !item.route_card,
          );
          if (itemsWithoutRouteCard.length > 0) {
            displayToast(
              "Cannot finish job card before finishing all route cards",
              "error",
            );
            return;
          }

          setIsLoading(true);
          try {
            const response = await fetchRouteCard(
              transactionData?.headDetails?.tran_id,
            );
            const head = response?.head_data || response?.data?.head_data;
            const status = String(
              head?.finish || head?.status || head?.route_card_finish || "",
            ).toLowerCase();
            const isFinished = [
              "finished",
              "finish",
              "completed",
              "complete",
            ].includes(status);

            if (!isFinished) {
              displayToast(
                "Cannot finish job card before finishing all route cards",
                "error",
              );
              return;
            }
          } finally {
            setIsLoading(false);
          }
        }
      }
      const result = await updateJobCardStatus(
        fieldName,
        transactionData?.headDetails?.tran_id,
      );

      if (result?.description || result.message) {
        displayToast(
          result?.description ||
            result.message ||
            "An unexpected error occurred while updating the jobcard ",
          "error",
        );
        return;
      }

      let statusMessage = "";
      let updatedStatus = "";

      if (fieldName === "start") {
        statusMessage = "Job card started successfully";
        updatedStatus = "started";
      } else if (fieldName === "finish") {
        statusMessage = "Job card finished successfully";
        updatedStatus = "finished";
      }

      if (result?.status === "success") {
        const updatedHeader = {
          ...transactionData.headDetails,
          status: updatedStatus,
        };
        displayToast(statusMessage, "success");
        setTransactionData((prev) => ({
          ...prev,
          headDetails: {
            ...prev.headDetails,
            status: updatedStatus,
          },
        }));
        setPrimaryDetails(updatedHeader);
        if (fieldName === "finish") {
          navigate(-1);
        }
      }
    } catch (error) {
      displayToast("An error occurred while updating the job card", "error");
    }
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(`You don't have permission to delete ${category}`, "error");
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const handleDeleteJobCardTransaction = async () => {
    try {
      const response = await deleteTransactionByCode(
        transactionCode,
        transactionData?.headDetails?.tran_id,
      );

      if (response.description || response.message) {
        displayToast(
          response.description ||
            response.message ||
            "Unexpected error while deleting transaction",
          "error",
        );
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigate(-1);
      }
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handlePrintClick = () => {
    if (!rights.can_print) {
      displayToast(`You don't have permission to print ${category}`, "error");
      return;
    }

    if (
      !transactionData.headDetails ||
      Object.keys(transactionData.headDetails).length === 0
    ) {
      displayToast("No transaction data available to print", "error");
      return;
    }

    const fileName = "invoice";

    navigate(`/${tab}/${category}/transactions/${fileName}`, {
      state: {
        formData: {
          headDetails: transactionData.headDetails,
          itemDetails: transactionData.itemDetails,
          rowData: tableData?.rows || [],
          transactionCode: transactionCode,
        },
      },
    });
  };

  const handleEditClick = () => {
    if (!rights.can_edit) {
      displayToast(`You don't have permission to edit ${category}`, "error");
      return;
    }

    setIsEditMode(true);
    displayToast(
      "Form is now in edit mode. You can modify and update the transaction.",
      "info",
    );
  };

  useEffect(() => {
    document.body.classList.add("job-card-mobile-layout");

    return () => {
      document.body.classList.remove("job-card-mobile-layout");
    };
  }, []);

  useEffect(() => {
    if (transactionCode) {
      fetchFieldMaster();
      if (
        !isEditMode &&
        Object.keys(transactionData.headDetails).length === 0
      ) {
        clearForm();
      }
    }
    if (transactionId && transactionCode) {
      fetchFieldMasterTransactionData(transactionCode, transactionId);
    }
  }, [transactionId, transactionCode]);

  const fetchFieldMasterTransactionData = async (
    transactionCode,
    transactionId,
  ) => {
    if (!isEditMode) {
      setPrimaryDetails({});
    }

    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetchJobCardByCodeAndId(
        transactionCode,
        transactionId,
      );

      if (response.description) {
        displayToast(response.description, "error");
      } else {
        const headDetails = response.head_data || {};
        const itemDetails = response.item_data || [];

        if (location.state?.transactionId) {
          setTransactionData({
            headDetails,
            itemDetails,
          });

          if (!isEditMode) {
            setPrimaryDetails(headDetails);
            setTransactionItems(itemDetails);
          }
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        const headFields = response.items?.head_fields || [];
        const rows = response.items?.item_fields || [];
        const extraForms = response.items?.extra_forms || [];

        const updatedHeadFields = [...headFields];
        setTableData({
          head_fields: updatedHeadFields,
          rows,
          extra_forms: extraForms,
        });
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="job-card-container">
          <header
            className="sales-form-header"
            style={{
              backgroundColor: colorCode ? `#${colorCode}` : "var(--primary)",
            }}
          >
            <div className="sales-form-header-heading">
              {!isEditMode && transactionId && (
                <button
                  className="back-button-transaction"
                  onClick={handleBackClick}
                >
                  <ArrowLeft size={25} />
                </button>
              )}
              <img
                src={category === "enquiry" ? Enquiry : Quotation}
                className="sales-form-header-icon"
                alt="Form Type"
              />
              <h5>
                {capitalizeFirstLetterOfEachWord(category)}
                {isEditMode && " - Edit Mode"}
              </h5>
            </div>
            <div className="sales-form-header-heading">
              <div className="jobcard-main-button-container">
                {["start", "finish"].map((fieldName) => {
                  const status = transactionData?.headDetails?.status;
                  const isStart = fieldName === "start";
                  const disableStart =
                    status === "started" ||
                    status === "finished" ||
                    Object.keys(transactionData?.headDetails || {}).length ===
                      0;
                  const disableFinish =
                    status !== "started" || status === "finished";

                  return (
                    <button
                      key={fieldName}
                      className="jobcard-main-button"
                      onClick={() => handleJobCardAction(fieldName)}
                      disabled={isStart ? disableStart : disableFinish}
                    >
                      {isStart
                        ? status === "started" || status === "finished"
                          ? "Started"
                          : "Start"
                        : status === "finished"
                          ? "Finished"
                          : "Finish"}
                    </button>
                  );
                })}
              </div>

              {isEditMode || (!transactionId && !isEditMode) ? (
                <X
                  className="sales-form-icons cursor-pointer"
                  onClick={
                    !transactionId && !isEditMode
                      ? handleCloseAddMode
                      : handleCloseEditMode
                  }
                  size={24}
                  title="Close Mode"
                />
              ) : (
                transactionCode &&
                transactionId && (
                  <>
                    {transactionData.headDetails.status !== "finished" && (
                      <img
                        src={Edit}
                        className="sales-form-icons"
                        alt="Edit"
                        onClick={handleEditClick}
                        style={{
                          cursor: rights.can_edit ? "pointer" : "not-allowed",
                          opacity: rights.can_edit ? 1 : 0.5,
                        }}
                        title={
                          rights.can_edit
                            ? "Edit Transaction"
                            : "No permission to edit"
                        }
                      />
                    )}
                    <img
                      src={Delete}
                      className="sales-form-icons"
                      alt="Delete"
                      onClick={handleDeleteClick}
                      style={{
                        cursor: rights.can_delete ? "pointer" : "not-allowed",
                        opacity: rights.can_delete ? 1 : 0.5,
                      }}
                      title={
                        rights.can_delete
                          ? "Delete Transaction"
                          : "No permission to delete"
                      }
                    />
                    <img
                      src={Print}
                      className="sales-form-icons"
                      style={{
                        cursor: rights.can_print ? "pointer" : "not-allowed",
                        opacity: rights.can_print ? 1 : 0.5,
                      }}
                      onClick={handlePrintClick}
                      title={
                        rights.can_print
                          ? "Print Transaction"
                          : "No permission to print"
                      }
                      alt="Print"
                    />
                  </>
                )
              )}
            </div>
          </header>

          <PrimaryDocument
            transactionCode={transactionCode}
            headFields={tableData.head_fields}
            extraForms={tableData.extra_forms}
            isViewMode={isViewMode}
            type="JobCard"
            isJobCardInEditMode={isEditMode}
            isAutoTransactionNo={isAutoTransactionNo}
            backDatedEntry={backDatedEntry}
          />
          <TransactionTable
            type="JobCard"
            rowData={tableData?.rows || []}
            headFields={tableData.head_fields}
            transactionCode={transactionCode || ""}
            isViewMode={isViewMode}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            setTransactionData={setTransactionData}
            transactionData={transactionData}
            isAutoTransactionNo={isAutoTransactionNo}
            pickupMandatory={pickupMandatory}
            onRefresh={() =>
              fetchFieldMasterTransactionData(transactionCode, transactionId)
            }
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteJobCardTransaction}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default JobCard;
